import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Activity, Briefcase, Building2, CheckCircle2, ChevronLeft, ChevronRight, Copy, Eye, FileText, FolderOpen, Info, Loader2, Phone, Plus, Save, Shield, Sparkles, Users, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const CaseBuilderPage = () => {
  const [step, setStep] = useState('start'); // start, interview, subjects, documents, complete
  const [caseData, setCaseData] = useState(null);
  const [caseType, setCaseType] = useState('personal_injury');
  const [jurisdiction, setJurisdiction] = useState('california');
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState({});
  const [subjects, setSubjects] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('demand_letter');
  const [newSubject, setNewSubject] = useState({ type: 'plaintiff', data: {} });
  const [showSubjectForm, setShowSubjectForm] = useState(false);
  const { trackEvent } = useEngagementTracker();
  
  const CASE_TYPE_OPTIONS = [
    { value: 'personal_injury', label: 'Personal Injury' },
    { value: 'employment', label: 'Employment' },
    { value: 'contract', label: 'Contract Dispute' },
    { value: 'default', label: 'Other Civil Matter' }
  ];
  
  const JURISDICTION_OPTIONS = [
    { value: 'california', label: 'California' },
    { value: 'new_york', label: 'New York' },
    { value: 'texas', label: 'Texas' },
    { value: 'florida', label: 'Florida' },
    { value: 'illinois', label: 'Illinois' },
    { value: 'federal', label: 'Federal' }
  ];
  
  const SUBJECT_TYPES = [
    { value: 'plaintiff', label: 'Plaintiff', icon: Users },
    { value: 'defendant', label: 'Defendant', icon: Building2 },
    { value: 'witness', label: 'Witness', icon: Eye },
    { value: 'expert', label: 'Expert Witness', icon: Briefcase },
    { value: 'medical_provider', label: 'Medical Provider', icon: Activity },
    { value: 'insurance_company', label: 'Insurance Company', icon: Shield }
  ];
  
  const DOCUMENT_TYPES = [
    { value: 'demand_letter', label: 'Demand Letter', desc: 'Pre-litigation demand' },
    { value: 'complaint', label: 'Complaint', desc: 'Formal court filing' },
    { value: 'interview_summary', label: 'Interview Summary', desc: 'Case intake summary' },
    { value: 'witness_statement', label: 'Witness Statement', desc: 'Witness declaration' },
    { value: 'discovery_requests', label: 'Discovery Requests', desc: 'Interrogatories & RFPs' }
  ];

  useEffect(() => {
    trackEvent('case-builder', 'view');
  }, [trackEvent]);

  const startCase = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/case-builder/start`, {
        case_type: caseType,
        mode: 'attorney',
        jurisdiction: jurisdiction
      });
      setCaseData(res.data);
      setStep('interview');
      trackEvent('case-builder', 'case_started', { case_type: caseType });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveResponses = async (sectionId, sectionResponses) => {
    if (!caseData) return;
    try {
      await axios.post(`${API}/case-builder/response`, {
        case_id: caseData.case_id,
        section_id: sectionId,
        responses: sectionResponses
      });
      setResponses(prev => ({ ...prev, [sectionId]: sectionResponses }));
    } catch (e) {
      console.error(e);
    }
  };

  const addSubject = async (subjectType, subjectData) => {
    if (!caseData) return;
    try {
      const res = await axios.post(`${API}/case-builder/subject`, {
        case_id: caseData.case_id,
        subject_type: subjectType,
        subject_data: subjectData
      });
      setSubjects(prev => [...prev, { ...subjectData, subject_type: subjectType, subject_id: res.data.subject_id }]);
      trackEvent('case-builder', 'subject_added', { subject_type: subjectType });
    } catch (e) {
      console.error(e);
    }
  };

  const generateDocument = async () => {
    if (!caseData) return;
    setGeneratingDoc(true);
    try {
      const res = await axios.post(`${API}/case-builder/generate-document`, {
        case_id: caseData.case_id,
        document_type: selectedDocType,
        use_multi_model: true
      });
      setDocuments(prev => [...prev, res.data]);
      trackEvent('case-builder', 'document_generated', { document_type: selectedDocType });
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingDoc(false);
    }
  };

  const renderStartStep = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-blue-600" />
          Start New Case
        </CardTitle>
        <CardDescription>Select case type and jurisdiction to begin the interview</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Case Type</Label>
          <Select value={caseType} onValueChange={setCaseType}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {CASE_TYPE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Jurisdiction</Label>
          <Select value={jurisdiction} onValueChange={setJurisdiction}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {JURISDICTION_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={startCase} disabled={loading} className="w-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          Start Case Interview
        </Button>
      </CardContent>
    </Card>
  );

  const renderInterviewStep = () => {
    if (!caseData?.interview_sections) return null;
    const sections = caseData.interview_sections;
    const section = sections[currentSection];
    const sectionResponses = responses[section?.id] || {};

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{section?.title}</CardTitle>
              <CardDescription>Section {currentSection + 1} of {sections.length}</CardDescription>
            </div>
            <Badge variant="outline">{caseData.case_type}</Badge>
          </div>
          <Progress value={((currentSection + 1) / sections.length) * 100} className="mt-2" />
        </CardHeader>
        <CardContent className="space-y-4">
          {section?.questions?.map(q => (
            <div key={q.id}>
              <Label className="flex items-center gap-1">
                {q.question}
                {q.required && <span className="text-red-500">*</span>}
              </Label>
              {q.type === 'textarea' ? (
                <Textarea
                  value={sectionResponses[q.id] || ''}
                  onChange={e => setResponses(prev => ({
                    ...prev,
                    [section.id]: { ...sectionResponses, [q.id]: e.target.value }
                  }))}
                  placeholder={`Enter ${q.question.toLowerCase()}`}
                  className="mt-1"
                />
              ) : q.type === 'select' ? (
                <Select
                  value={sectionResponses[q.id] || ''}
                  onValueChange={v => setResponses(prev => ({
                    ...prev,
                    [section.id]: { ...sectionResponses, [q.id]: v }
                  }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {q.options?.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : q.type === 'boolean' ? (
                <Select
                  value={sectionResponses[q.id]?.toString() || ''}
                  onValueChange={v => setResponses(prev => ({
                    ...prev,
                    [section.id]: { ...sectionResponses, [q.id]: v === 'true' }
                  }))}
                >
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              ) : q.type === 'number' ? (
                <Input
                  type="number"
                  value={sectionResponses[q.id] || ''}
                  onChange={e => setResponses(prev => ({
                    ...prev,
                    [section.id]: { ...sectionResponses, [q.id]: parseFloat(e.target.value) || 0 }
                  }))}
                  placeholder="0"
                  className="mt-1"
                />
              ) : (
                <Input
                  type={q.type === 'date' ? 'date' : 'text'}
                  value={sectionResponses[q.id] || ''}
                  onChange={e => setResponses(prev => ({
                    ...prev,
                    [section.id]: { ...sectionResponses, [q.id]: e.target.value }
                  }))}
                  className="mt-1"
                />
              )}
            </div>
          ))}
        </CardContent>
        <div className="p-4 border-t flex justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => saveResponses(section.id, sectionResponses)}
            >
              Save Section
            </Button>
            {currentSection < sections.length - 1 ? (
              <Button onClick={() => {
                saveResponses(section.id, sectionResponses);
                setCurrentSection(prev => prev + 1);
              }}>
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={() => {
                saveResponses(section.id, sectionResponses);
                setStep('subjects');
              }}>
                Continue to Subjects <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderSubjectsStep = () => {
    const getSubjectFields = (type) => {
      if (type === 'plaintiff') {
        return [
          { id: 'full_name', label: 'Full Name', required: true },
          { id: 'date_of_birth', label: 'Date of Birth', type: 'date' },
          { id: 'address', label: 'Address' },
          { id: 'phone', label: 'Phone' },
          { id: 'email', label: 'Email' },
          { id: 'occupation', label: 'Occupation' }
        ];
      } else if (type === 'defendant') {
        return [
          { id: 'full_name', label: 'Name/Entity', required: true },
          { id: 'entity_type', label: 'Type', type: 'select', options: ['Individual', 'Corporation', 'LLC', 'Partnership'] },
          { id: 'address', label: 'Address' },
          { id: 'relationship', label: 'Relationship to Case' },
          { id: 'insurance_carrier', label: 'Insurance Carrier' },
          { id: 'policy_limits', label: 'Policy Limits ($)', type: 'number' }
        ];
      } else {
        return [
          { id: 'full_name', label: 'Full Name', required: true },
          { id: 'contact_info', label: 'Contact Info' },
          { id: 'relationship', label: 'Relationship to Case' },
          { id: 'what_witnessed', label: 'What They Witnessed', type: 'textarea' }
        ];
      }
    };
    
    const fields = getSubjectFields(newSubject.type);
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Case Subjects
          </CardTitle>
          <CardDescription>Add plaintiffs, defendants, witnesses, and other parties</CardDescription>
        </CardHeader>
        <CardContent>
          {subjects.length > 0 && (
            <div className="mb-4 space-y-2">
              {subjects.map((s, i) => (
                <div key={i} className="p-3 border rounded-lg flex items-center justify-between">
                  <div>
                    <Badge variant="outline" className="mr-2">{s.subject_type}</Badge>
                    <span className="font-medium">{s.full_name || s.provider_name || s.company_name}</span>
                  </div>
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
              ))}
            </div>
          )}
          
          {!showSubjectForm ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {SUBJECT_TYPES.map(st => (
                <Button
                  key={st.value}
                  variant="outline"
                  className="h-auto py-3 flex flex-col items-center gap-1"
                  onClick={() => {
                    setNewSubject({ type: st.value, data: {} });
                    setShowSubjectForm(true);
                  }}
                >
                  <st.icon className="h-5 w-5" />
                  <span className="text-xs">{st.label}</span>
                </Button>
              ))}
            </div>
          ) : (
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-lg">Add {newSubject.type}</Label>
                <Button variant="ghost" size="sm" onClick={() => setShowSubjectForm(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-3">
                {fields.map(f => (
                  <div key={f.id}>
                    <Label>{f.label} {f.required && <span className="text-red-500">*</span>}</Label>
                    {f.type === 'textarea' ? (
                      <Textarea
                        value={newSubject.data[f.id] || ''}
                        onChange={e => setNewSubject(prev => ({
                          ...prev,
                          data: { ...prev.data, [f.id]: e.target.value }
                        }))}
                        className="mt-1"
                      />
                    ) : f.type === 'select' ? (
                      <Select
                        value={newSubject.data[f.id] || ''}
                        onValueChange={v => setNewSubject(prev => ({
                          ...prev,
                          data: { ...prev.data, [f.id]: v }
                        }))}
                      >
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {f.options?.map(opt => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type={f.type || 'text'}
                        value={newSubject.data[f.id] || ''}
                        onChange={e => setNewSubject(prev => ({
                          ...prev,
                          data: { ...prev.data, [f.id]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }
                        }))}
                        className="mt-1"
                      />
                    )}
                  </div>
                ))}
                <Button
                  onClick={() => {
                    addSubject(newSubject.type, newSubject.data);
                    setNewSubject({ type: 'plaintiff', data: {} });
                    setShowSubjectForm(false);
                  }}
                  className="w-full mt-2"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add {newSubject.type.charAt(0).toUpperCase() + newSubject.type.slice(1)}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
        <div className="p-4 border-t flex justify-between">
          <Button variant="outline" onClick={() => setStep('interview')}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Interview
          </Button>
          <Button onClick={() => setStep('documents')}>
            Continue to Documents <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </Card>
    );
  };

  const renderDocumentsStep = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Documents
          </CardTitle>
          <CardDescription>Generate legal documents from your case data using multi-AI verification</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Document Type</Label>
            <Select value={selectedDocType} onValueChange={setSelectedDocType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOCUMENT_TYPES.map(dt => (
                  <SelectItem key={dt.value} value={dt.value}>
                    <div>
                      <div className="font-medium">{dt.label}</div>
                      <div className="text-xs text-muted-foreground">{dt.desc}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={generateDocument} disabled={generatingDoc} className="w-full">
            {generatingDoc ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Generating with Multi-AI Verification...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Generate Document
              </>
            )}
          </Button>
        </CardContent>
      </Card>
      
      {documents.map((doc, i) => (
        <Card key={i}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{doc.document_type?.replace(/_/g, ' ').toUpperCase()}</CardTitle>
              <div className="flex items-center gap-2">
                {doc.verification?.confidence && (
                  <Badge className={doc.verification.confidence >= 70 ? 'bg-green-500' : 'bg-amber-500'}>
                    {doc.verification.confidence}% Confidence
                  </Badge>
                )}
                <Button variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(doc.content)}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 p-4 rounded-lg whitespace-pre-wrap text-sm font-mono max-h-96 overflow-y-auto">
              {doc.content}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-50" data-testid="case-builder-page">
      <Disclaimer />
      <div className="container-shell py-8">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Case Builder</h1>
            <Badge className="bg-blue-600">NEW</Badge>
            <Badge variant="outline">Multi-AI</Badge>
          </div>
          <p className="text-muted-foreground">Interactive case intake with multi-subject support and AI-powered document generation</p>
        </div>
        
        {/* Progress indicator */}
        {step !== 'start' && (
          <div className="flex items-center gap-2 mb-6">
            {['interview', 'subjects', 'documents'].map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step === s ? 'bg-blue-600 text-white' : 
                  ['interview', 'subjects', 'documents'].indexOf(step) > i ? 'bg-green-500 text-white' : 'bg-slate-200'
                }`}>
                  {['interview', 'subjects', 'documents'].indexOf(step) > i ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </div>
                {i < 2 && <div className={`w-12 h-1 mx-1 ${['interview', 'subjects', 'documents'].indexOf(step) > i ? 'bg-green-500' : 'bg-slate-200'}`} />}
              </div>
            ))}
          </div>
        )}
        
        {step === 'start' && renderStartStep()}
        {step === 'interview' && renderInterviewStep()}
        {step === 'subjects' && renderSubjectsStep()}
        {step === 'documents' && renderDocumentsStep()}
      </div>
      <Footer />
    </main>
  );
};


export default CaseBuilderPage;
